
import type { NLPSearchResultSource } from '../../ai-answer-dialog/types';


import { extractModelName } from './extract-model-name';
import { extractUniquePluginNames } from './extract-unique-plugin-names';
import { filterNonSearchMinions } from './filter-non-search-minions';
import { filterNonSearchPlugins } from './filter-non-search-plugins';
import { getMinionOutputItem } from './get-minion-output-item';
import { getRankedDocumentsWithChunks } from './get-ranked-documents-with-chunks';
import { getSearchResultsFromMinions } from './get-search-results-from-minions';
import { getSearchResultsFromPlugins } from './get-search-results-from-plugins';
import { isOrchestrationStructure } from './is-orchestration-structure';
import { mergeSearchExtraInfo } from './merge-search-extra-info';
import { normalizeCacheHit } from './normalize-cache-hit';
import { parsePageSearchOutput } from './parse-page-search-output';
import { parseTeamworkOutput } from './parse-teamwork-output';
import { type FunctionResultType, type MinionOutput, type MinionOverviewInfo, type MinionPluginInvocationInfo, type NormalizedDebuggerData, type OrchestrationRound, type ParsedMinionData, type ParsedPluginOutput, type PluginExtraInfo, PluginName, type RawDebuggerData } from './types';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getSearchResultsFromPlugins } from './get-search-results-from-plugins';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getSearchResultsFromMinions } from './get-search-results-from-minions';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { mergeSearchExtraInfo } from './merge-search-extra-info';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getRankedDocumentsWithChunks } from './get-ranked-documents-with-chunks';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { parseTeamworkOutput } from './parse-teamwork-output';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { parsePeopleOutput } from './parse-people-output';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { parsePageSearchOutput } from './parse-page-search-output';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getPluginTagColor } from './get-plugin-tag-color';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { extractModelName } from './extract-model-name';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { extractUniquePluginNames } from './extract-unique-plugin-names';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { filterNonSearchPlugins } from './filter-non-search-plugins';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { filterNonSearchMinions } from './filter-non-search-minions';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { normalizeCacheHit } from './normalize-cache-hit';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getMinionOutputItem } from './get-minion-output-item';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isOrchestrationStructure } from './is-orchestration-structure';

type ParsedMarkdownLink = {
	name: string;
	url: string;
	accountId: string | null;
};

/**
 * Parses a markdown-style link and extracts the display name and accountId.
 *
 * Handles links in format: [Name](/people/accountId)
 * Returns the name with accountId appended in parentheses if found.
 *
 * @param text - The text that may contain a markdown link
 * @returns Object with name, url, accountId, or null if not a valid link
 */
export const parseMarkdownLink = (text: string): ParsedMarkdownLink | null => {
	const match = text.match(/\[([^\]]+)\]\(([^)]+)\)/);
	if (!match) {
		return null;
	}

	const [, name, url] = match;
	const accountIdMatch = url.match(/\/people\/([^/]+)/);
	const accountId = accountIdMatch ? accountIdMatch[1] : null;

	return { name, url, accountId };
};

/**
 * Formats a markdown link as display text with accountId.
 *
 * @param text - The text that may contain a markdown link
 * @returns Formatted string like "Name (accountId)" or original text if not a link
 */
export const formatMarkdownLinkAsText = (text: string): string => {
	const parsed = parseMarkdownLink(text);
	if (!parsed) {
		return text;
	}
	return parsed.accountId ? `${parsed.name} (${parsed.accountId})` : parsed.name;
};

export const parsePluginOutput = (
	pluginName: string | undefined,
	output: string,
): ParsedPluginOutput | null => {
	switch (pluginName) {
		case PluginName.TEAMWORK: {
			const teamworkData = parseTeamworkOutput(output);
			if (teamworkData) {
				return { type: 'teamwork', data: teamworkData };
			}
			break;
		}
		case PluginName.PAGE_SEARCH_PLUGIN: {
			const pageSearchData = parsePageSearchOutput(output);
			if (pageSearchData) {
				return { type: 'pageSearch', data: pageSearchData };
			}
			break;
		}
	}
	return null;
};

/**
 * Parses minion_extra_info to extract displayable data.
 *
 * The minion_extra_info can contain different structures depending on the minion type:
 *
 * 1. Search Minion with functionMessage:
 *    - Extracts pluginInvocationInfo.extraInfo containing searchResults/rankedDocuments
 *    - Stores in searchExtraInfo for specialized rendering
 *
 * 2. Non-Search Minion with functionMessage:
 *    - Extracts pluginInvocationInfo.output or functionMessage.output
 *    - Attempts to parse it using parsePluginOutput for structured display
 *
 * 3. Teamwork Agent with results array:
 *    - Extracts the results array containing function invocation results
 *    - Each result has pluginInvocationInfo with name, status, and output
 *
 * 4. Content fallback:
 *    - If none of the above, extracts the content field as plain text
 *
 * 5. Parse failure:
 *    - If JSON parsing fails, displays the raw minion_extra_info string
 */
export const parseMinionExtraInfo = (
	minionExtraInfo: string | undefined,
	minionId: string | undefined,
	isSearchMinion: boolean,
): ParsedMinionData => {
	let extraInfo: MinionPluginInvocationInfo | string | null = null;
	let parsedOutput: ParsedPluginOutput | null = null;
	let functionResults: FunctionResultType[] | null = null;
	let searchExtraInfo: PluginExtraInfo | null = null;

	if (!minionExtraInfo) {
		return { extraInfo, parsedOutput, functionResults, searchExtraInfo };
	}

	try {
		const parsedExtraInfo = JSON.parse(minionExtraInfo);

		// Case 1 & 2: Has functionMessage (single function call result)
		if (parsedExtraInfo.functionMessage) {
			const { pluginInvocationInfo, output } = parsedExtraInfo.functionMessage;

			if (isSearchMinion && pluginInvocationInfo?.extraInfo) {
				// Search minion: extract nested search results from extraInfo
				try {
					searchExtraInfo = JSON.parse(pluginInvocationInfo.extraInfo);
				} catch {
					extraInfo = pluginInvocationInfo.extraInfo;
				}
			} else {
				// Non-search minion: parse the output for structured display
				const rawOutput = pluginInvocationInfo?.output || output;
				if (typeof rawOutput === 'string') {
					parsedOutput = parsePluginOutput(minionId, rawOutput);
					if (!parsedOutput) {
						extraInfo = rawOutput;
					}
				}
			}
		}
		// Case 3: Has results array (multiple function calls, e.g., Teamwork Agent)
		else if (Array.isArray(parsedExtraInfo.results) && parsedExtraInfo.results.length > 0) {
			functionResults = parsedExtraInfo.results as FunctionResultType[];
		}
		// Case 4: Has content field (plain text fallback)
		else if (parsedExtraInfo.content) {
			extraInfo = parsedExtraInfo.content;
		}
	} catch {
		// Case 5: JSON parsing failed, use raw string
		extraInfo = minionExtraInfo;
	}

	return { extraInfo, parsedOutput, functionResults, searchExtraInfo };
};

/**
 * Organizes minion outputs into orchestration rounds.
 * If the structure is already rounds (array of arrays), returns as-is.
 * If it's a flat array, wraps it in a single round.
 */
export const getOrchestrationRounds = (
	minionOutputs: MinionOutput[] | undefined,
): OrchestrationRound[] => {
	if (!Array.isArray(minionOutputs) || minionOutputs.length === 0) {
		return [];
	}

	// Check if it's already a rounds structure (array of arrays)
	if (isOrchestrationStructure(minionOutputs)) {
		return minionOutputs as OrchestrationRound[];
	}

	// Wrap flat array in a single round
	return [minionOutputs];
};

/**
 * Creates a high-level overview of a minion, excluding detailed output.
 * Used for the Orchestration Overview tab.
 */
export const getMinionOverviewInfo = (minion: MinionOutput): MinionOverviewInfo => {
	const minionInfo = getMinionOutputItem(minion);
	return {
		minion_id: minionInfo.minion_id,
		minion_input: minionInfo.minion_input,
		minion_extra_info: minionInfo.minion_extra_info,
	};
};

// --- Main Normalization Function ---

/**
 * Normalizes all debugger data from raw extra attributes.
 * This is the single entry point for preparing data for all debugger panels.
 *
 * Search results are extracted from both plugin invocations and minion outputs,
 * with plugin data taking precedence when available.
 *
 * @param extraAttributes - Raw extra attributes from the AI Answer context
 * @param sources - Sources from commonAttributes.sources
 * @returns Normalized data object with all data needed for each debugger panel
 */
export const normalizeDebuggerData = (
	extraAttributes: RawDebuggerData | undefined,
	sources?: NLPSearchResultSource[],
): NormalizedDebuggerData => {
	const {
		originalQuery,
		transformedQuery,
		traceId,
		answerGenerationModel,
		cacheInfo,
		sources: extraSources = [],
		pluginInvocations = [],
		minion_outputs = [],
		rawJson,
	} = extraAttributes ?? {};

	const resolvedSources = sources !== undefined ? sources : extraSources;

	// Extract search data from both plugins and minions, then merge
	const pluginSearchInfo = getSearchResultsFromPlugins(pluginInvocations);
	const minionSearchInfo = getSearchResultsFromMinions(minion_outputs);
	const searchExtraInfo = mergeSearchExtraInfo(pluginSearchInfo, minionSearchInfo);
	const rankedDocumentsWithChunks = getRankedDocumentsWithChunks(searchExtraInfo);

	const orchestrationRounds = getOrchestrationRounds(minion_outputs);

	return {
		requestOverview: {
			originalQuery: originalQuery ?? null,
			transformedQuery: transformedQuery ?? null,
			traceId: traceId ?? null,
			modelName: extractModelName(answerGenerationModel),
			cacheHit: normalizeCacheHit(cacheInfo),
			sources: resolvedSources,
			invokedPluginNames: extractUniquePluginNames(pluginInvocations),
		},
		rankedSearch: {
			searchExtraInfo,
			rankedDocumentsWithChunks,
		},
		rawSearch: {
			searchResults: searchExtraInfo?.searchResults ?? [],
		},
		pluginInvocations: {
			allPluginInvocations: pluginInvocations,
			nonSearchPluginInvocations: filterNonSearchPlugins(pluginInvocations),
			hasPluginInvocations: pluginInvocations ? pluginInvocations.length > 0 : false,
		},
		minions: {
			allMinionOutputs: minion_outputs,
			nonSearchMinionOutputs: filterNonSearchMinions(minion_outputs),
			hasMinionOutputs: minion_outputs ? minion_outputs.length > 0 : false,
			orchestrationRounds,
			roundCount: orchestrationRounds.length,
		},
		rawJson: {
			rawJson,
		},
	};
};
